import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { UserRoleService } from '../../core/services/user-role.service';

interface PermissionSection {
  name: string;
  expanded: boolean;
}
interface FeaturePermission {
  name: string;
  subtitle?: string;
  addCreate: 'yes' | 'no' | 'na';
  view: 'yes' | 'no' | 'na';
  editUpdate: 'yes' | 'no' | 'na';
  delete: 'yes' | 'no' | 'na';
}

@Component({
  standalone: true,
  selector: 'app-view-admin-role-permissions',
  imports: [CommonModule, RouterLink],
  templateUrl: './view-admin-role-permissions.component.html',
  styleUrl: './view-admin-role-permissions.component.css',
})
export class ViewAdminRolePermissionsComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private userRoleService = inject(UserRoleService);

  roleName = '';
  roleType: 'Base Role' | 'Custom Role' = 'Base Role';

  permissionSections: PermissionSection[] = [
    { name: 'Review Management', expanded: false },
    { name: 'Schedule Review', expanded: false },
    { name: 'Review Cycle', expanded: false },
    { name: 'Issue Management', expanded: false },
    { name: 'Action Plan', expanded: false },
    { name: 'Requests', expanded: false },
    { name: 'Dashboard', expanded: false },
    { name: 'Reports', expanded: false },
    { name: 'Help', expanded: false },
    { name: 'Master Data (Reference Data)', expanded: false },
    { name: 'Master Data (Risk Hierarchy)', expanded: false },
    { name: 'Master Data (System Config)', expanded: false },
    { name: 'Admin Panel (Roles & Permissions)', expanded: false },
    { name: 'User Management', expanded: false },
  ];

  // Review Management features with permissions
  reviewManagementFeatures: FeaturePermission[] = [
    { name: 'Standard Review', addCreate: 'yes', view: 'yes', editUpdate: 'no', delete: 'no' },
    { name: 'Go-Live Review', addCreate: 'yes', view: 'yes', editUpdate: 'no', delete: 'no' },
    { name: 'On-Demand Review', addCreate: 'yes', view: 'yes', editUpdate: 'no', delete: 'no' },
    { name: 'Special Project Review', addCreate: 'yes', view: 'yes', editUpdate: 'no', delete: 'no' },
    { name: 'Bulk Upload — Standard Review', subtitle: 'Template reviewer is mandatory', addCreate: 'na', view: 'yes', editUpdate: 'yes', delete: 'na' },
    { name: 'Review Listing — Main Menu', addCreate: 'na', view: 'yes', editUpdate: 'na', delete: 'na' },
    { name: 'Review Type Selection (Planning Page)', addCreate: 'yes', view: 'yes', editUpdate: 'na', delete: 'na' },
    { name: 'Export — Review Data', subtitle: 'Export to Excel', addCreate: 'na', view: 'yes', editUpdate: 'na', delete: 'na' },
  ];

  // Schedule Review features with permissions
  scheduleReviewFeatures: FeaturePermission[] = [
    { name: 'Schedule Review', 
      subtitle: 'Define fieldwork dates and target publication date', 
      addCreate: 'na', view: 'yes', editUpdate: 'yes', delete: 'na' },
  ];

  // Review Cycle features with permissions
  reviewCycleFeatures: FeaturePermission[] = [
    { name: 'Plan Review', addCreate: 'na', view: 'yes', editUpdate: 'yes', delete: 'na' },
    { name: 'Conduct Review', addCreate: 'na', view: 'yes', editUpdate: 'yes', delete: 'na' },
    { name: 'Report the Review', addCreate: 'na', view: 'yes', editUpdate: 'yes', delete: 'na' },
    { name: 'Download Template', addCreate: 'na', view: 'yes', editUpdate: 'na', delete: 'na' },
  ];
   issueManagementFeatures: FeaturePermission[] = [
    { name: 'Issue & Action Plan (Grid/Tab)', addCreate: 'na', view: 'yes', editUpdate: 'yes', delete: 'na' },
    { name: 'Export — Issue and Action Plan Data',subtitle:'Grid export', addCreate: 'na', view: 'yes', editUpdate: 'yes', delete: 'na' },
    { name: 'Approve / Reject Issue', subtitle:'Supervisor action on inactive issues', addCreate: 'na', view: 'yes', editUpdate: 'yes', delete: 'na' },
    { name: 'Bulk Upload — Issue & AP', addCreate: 'na', view: 'yes', editUpdate: 'na', delete: 'na' },
    { name: 'Issue Listing — Main Menu', addCreate: 'na', view: 'yes', editUpdate: 'na', delete: 'na' },
    { name: 'Export — Issue Data (Grid)', addCreate: 'na', view: 'yes', editUpdate: 'na', delete: 'na' },
    { name: 'Linked AP — Override Edit', addCreate: 'na', view: 'yes', editUpdate: 'na', delete: 'na' },
    { name: 'Export — Issue Data (Global Page)', addCreate: 'na', view: 'yes', editUpdate: 'na', delete: 'no' },
  ];
  actionPlanFeatures: FeaturePermission[] = [
    { name: 'Action Plan Listing — Main Menu', addCreate: 'na', view: 'yes', editUpdate: 'yes', delete: 'na' },
    { name: 'Linked Issue — Override Edit', addCreate: 'na', view: 'yes', editUpdate: 'yes', delete: 'na' },
  ];
  requestFeatures: FeaturePermission[] = [ 
    { name: 'Cancel Review', 
      subtitle: 'Reviewer/Supervisor: raise only. Manager: approve only. Admin: full access.',
      addCreate: 'na', view: 'yes', editUpdate: 'yes', delete: 'na' },
    { name: 'Not Applicable — Plan Review', 
      subtitle: 'Reviewer/Supervisor: raise only. Manager: approve only. Admin: full access.',
      addCreate: 'na', view: 'yes', editUpdate: 'yes', delete: 'na' },
    { name: 'Revised Target Publication Date', 
      subtitle: 'Reviewer/Supervisor: raise only. Manager: approve only. Admin: full access.',
      addCreate: 'na', view: 'yes', editUpdate: 'yes', delete: 'na' }, 
      { name: 'Accenture Operations SL — Add New Value', 
      addCreate: 'na', view: 'yes', editUpdate: 'yes', delete: 'na' },
      { name: 'Add New User', 
      addCreate: 'na', view: 'yes', editUpdate: 'yes', delete: 'na' },       
  ]
  dashboardFeatures: FeaturePermission[] = [
    { name: 'Dashboard',subtitle:'View summary and analytics', addCreate: 'na', view: 'yes', editUpdate: 'na', delete: 'na' },
  ];
  reportsFeatures: FeaturePermission[] = [
    { name: 'Reports', addCreate: 'na', view: 'yes', editUpdate: 'na', delete: 'na' },
  ];
   helpFeatures: FeaturePermission[] = [
    { name: 'Date Calculator', addCreate: 'na', view: 'yes', editUpdate: 'na', delete: 'na' },
  ];
  masterDataReferenceDataFeatures: FeaturePermission[] = [
    { name: 'Market & Market Unit', 
      subtitle: 'Admin only',
      addCreate: 'na', view: 'yes', editUpdate: 'yes', delete: 'na' 
    },
    { name: 'Accenture Operations Service Line', 
      subtitle: 'Admin only',
      addCreate: 'na', view: 'yes', editUpdate: 'yes', delete: 'na' 
    },
  
    { name: 'Delivery Centre (City)', 
      subtitle: 'Admin only',
      addCreate: 'na', view: 'yes', editUpdate: 'yes', delete: 'na' 
    },
     { name: 'Country', 
      subtitle: 'Admin only',
      addCreate: 'na', view: 'yes', editUpdate: 'yes', delete: 'na' 
    },
     { name: 'Type of Work / Review Type', 
      subtitle: 'Admin only',
      addCreate: 'na', view: 'yes', editUpdate: 'yes', delete: 'na' 
    },
     { name: 'Review Status', 
      subtitle: 'Admin only',
      addCreate: 'na', view: 'yes', editUpdate: 'yes', delete: 'na' 
    },
     { name: 'Overall Report Rating', 
      subtitle: 'Admin only',
      addCreate: 'na', view: 'yes', editUpdate: 'yes', delete: 'na' 
    },
    { name: 'Quarter', 
      subtitle: 'Admin only',
      addCreate: 'na', view: 'yes', editUpdate: 'yes', delete: 'na' 
    },
    { name: 'Service Line', 
      subtitle: 'Admin only',
      addCreate: 'na', view: 'yes', editUpdate: 'yes', delete: 'na' 
    },
    { name: 'Internal Dependency', 
      subtitle: 'Admin only',
      addCreate: 'na', view: 'yes', editUpdate: 'yes', delete: 'na' 
    },
    { name: 'RepLetter Conflict Values', 
      subtitle: 'Admin only',
      addCreate: 'na', view: 'yes', editUpdate: 'yes', delete: 'na' 
    },
    { name: 'RepLetter Questions with Conflict', 
      subtitle: 'Admin only',
      addCreate: 'na', view: 'yes', editUpdate: 'yes', delete: 'na' 
    },
  ];
   masterDataRiskHierarchyFeatures: FeaturePermission[] = [
    { name: 'Broad Issue Category (L1)', 
      subtitle: 'Admin only',
      addCreate: 'na', view: 'yes', editUpdate: 'yes', delete: 'na' 
    },
     { name: 'Detailed Issue Category (L2)', 
      subtitle: 'Admin only',
      addCreate: 'na', view: 'yes', editUpdate: 'yes', delete: 'na' 
    },
    { name: 'Risk Statement (L3)', 
      subtitle: 'Admin only',
      addCreate: 'na', view: 'yes', editUpdate: 'yes', delete: 'na' 
    },
  ];
  masterDataSystemConfigFeatures: FeaturePermission[] = [
    { name: 'Download Templates / File Management', 
      subtitle: 'Admin only',
      addCreate: 'na', view: 'yes', editUpdate: 'yes', delete: 'na' 
    },
    { name: 'Public Holiday Calendar', 
      subtitle: 'Admin only',
      addCreate: 'na', view: 'yes', editUpdate: 'yes', delete: 'na' 
    },
     { name: 'Email Template Management', 
      subtitle: 'Admin only',
      addCreate: 'na', view: 'yes', editUpdate: 'yes', delete: 'na' 
    },
     { name: 'Header & Footer Management', 
      subtitle: 'Admin only',
      addCreate: 'na', view: 'yes', editUpdate: 'yes', delete: 'na' 
    },
  ];
  adminPanelFeatures: FeaturePermission[] = [
    { name: 'Roles & Permissions Mgmt (Custom Role)', 
    //   subtitle: 'Admin only',
      addCreate: 'na', view: 'yes', editUpdate: 'yes', delete: 'na' 
    },
     { name:'Roles & Permissions Mgmt (Base Role)', 
      subtitle: 'View only for Admin',
      addCreate: 'na', view: 'yes', editUpdate: 'yes', delete: 'na' 
    },
     { name: 'Audit Log', 
      subtitle: 'Supervisor and above can view',
      addCreate: 'na', view: 'yes', editUpdate: 'yes', delete: 'na' 
    },
  ];
  userManagementFeatures: FeaturePermission[] = [
    { name: 'Add New User (Direct)', 
      subtitle: 'Admin only',
      addCreate: 'na', view: 'yes', editUpdate: 'yes', delete: 'na' 
    },
    { name: 'Edit User Details & Role Assignment', 
      subtitle: 'Admin only',
      addCreate: 'na', view: 'yes', editUpdate: 'yes', delete: 'na' 
    },
     { name: 'Activate / Deactivate User', 
      subtitle: 'Admin only',
      addCreate: 'na', view: 'yes', editUpdate: 'yes', delete: 'na' 
    },
    { name: 'Delete User', 
      subtitle: 'Admin only',
      addCreate: 'na', view: 'yes', editUpdate: 'yes', delete: 'na' 
    },
    { name: 'View All Users', 
      subtitle: 'Admin only',
      addCreate: 'na', view: 'yes', editUpdate: 'yes', delete: 'na' 
    },
  ];

  // Map section names to their features
  private sectionFeaturesMap: Record<string, FeaturePermission[]> = {
    'Review Management': this.reviewManagementFeatures,
    'Schedule Review': this.scheduleReviewFeatures,
    'Review Cycle': this.reviewCycleFeatures,
    'Issue Management': this.issueManagementFeatures,
    'Action Plan': this.actionPlanFeatures,
    'Requests':this.requestFeatures,
    'Dashboard': this.dashboardFeatures,
    'Reports': this.reportsFeatures,
    'Help': this.helpFeatures,
    'Master Data (Reference Data)': this.masterDataReferenceDataFeatures,
    'Master Data (Risk Hierarchy)': this.masterDataRiskHierarchyFeatures,
    'Master Data (System Config)': this.masterDataSystemConfigFeatures,
    'Admin Panel (Roles & Permissions)': this.adminPanelFeatures,
    'User Management': this.userManagementFeatures,

  };

  getSectionFeatures(sectionName: string): FeaturePermission[] {
    return this.sectionFeaturesMap[sectionName] || [];
  }

  hasSectionFeatures(sectionName: string): boolean {
    return !!this.sectionFeaturesMap[sectionName];
  }

  private readonly restrictedRoles = ['R&C Member', 'Reviewer', 'Supervisor', 'Manager'];

  // Sections that are read-only for roles other than 'Read Only (Non R&C)'
  private readonly readOnlySections = [
    'Master Data (Reference Data)',
    'Master Data (Risk Hierarchy)',
    'Master Data (System Config)',
    'Admin Panel (Roles & Permissions)',
    'User Management',
  ];

  isReadOnlySection(sectionName: string): boolean {
    // For 'Admin / SuperUser' role, all sections are clickable
    if (this.roleName === 'Admin / SuperUser') {
      return false;
    }
    // For all other roles, these sections are read-only
    return this.readOnlySections.includes(sectionName);
  }

  ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      this.roleName = params['roleName'] || 'R&C Member';
      this.roleType = params['roleType'] || 'Base Role';
    });
  }

  toggleSection(index: number): void {
    const section = this.permissionSections[index];
    // Don't toggle if section is read-only
    if (this.isReadOnlySection(section.name)) {
      return;
    }
    section.expanded = !section.expanded;
  }

  goBackToRoles(): void {
    this.router.navigate(['/admin']);
  }
}
